import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { ChevronDownIcon } from './Icons';

export type ActionMenuItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
  hidden?: boolean;
  separatorBefore?: boolean;
};

export function ActionMenu({
  label,
  items,
  size = 'sm',
  align = 'right',
  loading = false,
  disabled = false,
}: {
  label: string;
  items: ActionMenuItem[];
  size?: 'sm' | 'default';
  align?: 'left' | 'right';
  loading?: boolean;
  disabled?: boolean;
}) {
  const { colors } = useTheme();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const visible = items.filter((item) => !item.hidden);

  useEffect(() => {
    if (!open) return;

    function close() {
      setOpen(false);
    }

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      close();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }

    function onReposition() {
      if (!triggerRef.current) return;
      setRect(triggerRef.current.getBoundingClientRect());
    }

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  if (visible.length === 0) return null;

  const menuWidth = 248;

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size={size}
        loading={loading}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next && triggerRef.current) {
            setRect(triggerRef.current.getBoundingClientRect());
          }
        }}
      >
        {label}
        <ChevronDownIcon
          className={cn(
            'h-3.5 w-3.5 transition-transform',
            open && 'rotate-180',
          )}
        />
      </Button>
      {open && rect
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              className={cn(
                'fixed overflow-hidden rounded-xl border py-1 shadow-lg',
                colors.surface,
                colors.border,
              )}
              style={{
                ...zStyle(Z_INDEX.portaledDropdown),
                top: rect.bottom + 4,
                width: menuWidth,
                left:
                  align === 'right'
                    ? Math.max(8, rect.right - menuWidth)
                    : rect.left,
              }}
            >
              {visible.map((item) => (
                <div key={item.id}>
                  {item.separatorBefore ? (
                    <div className={cn('my-1 border-t', colors.border)} />
                  ) : null}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                      item.danger
                        ? 'text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40'
                        : cn(colors.text, 'hover:bg-surface-muted'),
                      item.disabled && 'pointer-events-none opacity-50',
                    )}
                    onClick={() => {
                      if (item.disabled) return;
                      setOpen(false);
                      item.onSelect();
                    }}
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
                      {item.icon}
                    </span>
                    {item.label}
                  </button>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
